import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { XYPosition } from 'reactflow';
import type { OnConnectStartParams } from 'reactflow';
import { ActionCreators as ReduxUndoActionCreators } from 'redux-undo';

import { loadExistingWorkflow } from '../../migration';
import type { NodeType } from '../../types';
import { createLabware, createTask, deleteNodesEdges } from '../actions';
import {
  addConsistencyTarget,
  addHardTimeConstraint,
  addTimingTarget,
  createLabwareDependencyEdge,
  createTaskDependency,
  deleteConsistencyTarget,
  deleteHardTimeConstraint,
  deleteTaskDependency,
  deleteTimingTarget,
  editHardTimeConstraint,
  editTimeConstraintData,
  editTimingTarget,
  moveNodes,
  setNodeColorIndex,
  updateLabwareSlotForTask,
} from '../graph/graphSlice';
import {
  addParameterDefinition,
  removeParameterDefinition,
  setComment,
  setParameterDefinitions,
  setSchedulerVersion,
  setWorkflowAuthor,
  setWorkflowDescription,
  setWorkflowName,
  unbindParameterDefinitionOfTask,
  unbindParameterDefinitions,
  updateExecutorOptions,
  updateLabwareProperties,
  updateParameterDefinition,
  updatePlannerOptions,
  updateTaskAppliedParameters,
  updateTaskParameters,
  updateTaskProperties,
} from '../workflow/workflowSlice';

export type SelectionPanelId = NodeType | 'viewSettings';

export interface UiState {
  flowPositionCenter: XYPosition;
  flowZoom: number;
  selectionDrag: boolean;
  selectionPanel: SelectionPanelId | null;
  isSaved: boolean;
  isDeleted: boolean;
  currentTimeConstraintConnectionInProgress: OnConnectStartParams | null;
  newlyCreatedTimeConstraintId: string | null;
  taskDependenciesAreVisible: boolean;
}

const initialState: UiState = {
  flowPositionCenter: { x: 0, y: 0 },
  flowZoom: 1,
  selectionDrag: false,
  selectionPanel: null,
  isSaved: true,
  isDeleted: false,
  currentTimeConstraintConnectionInProgress: null,
  newlyCreatedTimeConstraintId: null,
  taskDependenciesAreVisible: false,
};

const actionTypesThatUnlockSaving: string[] = [
  moveNodes.type,
  updateLabwareProperties.type,
  updateTaskProperties.type,
  updateTaskParameters.type,
  updateTaskAppliedParameters.type,
  updateLabwareSlotForTask.type,
  createLabware.type,
  createTask.type,
  setNodeColorIndex.type,
  createLabwareDependencyEdge.type,
  setComment.type,
  setSchedulerVersion.type,
  setWorkflowAuthor.type,
  setWorkflowDescription.type,
  setWorkflowName.type,
  deleteNodesEdges.type,
  editTimeConstraintData.type,
  createTaskDependency.type,
  deleteTaskDependency.type,
  addParameterDefinition.type,
  updateParameterDefinition.type,
  removeParameterDefinition.type,
  setParameterDefinitions.type,
  unbindParameterDefinitions.type,
  unbindParameterDefinitionOfTask.type,
  updatePlannerOptions.type,
  updateExecutorOptions.type,
  addHardTimeConstraint.type,
  editHardTimeConstraint.type,
  deleteHardTimeConstraint.type,
  addTimingTarget.type,
  editTimingTarget.type,
  deleteTimingTarget.type,
  addConsistencyTarget.type,
  deleteConsistencyTarget.type,
  ReduxUndoActionCreators.undo().type,
  ReduxUndoActionCreators.redo().type,
];

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    changeScreenPosition: (
      state,
      action: PayloadAction<{ position: XYPosition; zoom: number }>
    ) => {
      const { position, zoom } = action.payload;
      state.flowPositionCenter = position;
      state.flowZoom = zoom;
    },
    changeSelectionDragState: (state, action: PayloadAction<boolean>) => {
      state.selectionDrag = action.payload;
    },
    openSelectionPanel: (state, action: PayloadAction<SelectionPanelId>) => {
      state.selectionPanel = action.payload;
    },
    closeSelectionPanel: (state) => {
      state.selectionPanel = null;
    },
    setTaskDependenciesAreVisible: (state, action: PayloadAction<boolean>) => {
      state.taskDependenciesAreVisible = action.payload;
    },
    setCurrentTimeConstraintConnectionInProgress: (
      state,
      action: PayloadAction<OnConnectStartParams | null>
    ) => {
      state.currentTimeConstraintConnectionInProgress = action.payload;
    },
    setNewlyCreatedTimeConstraintId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.newlyCreatedTimeConstraintId = action.payload;
    },
    setWorkflowIsSaved: (state, action: PayloadAction<boolean>) => {
      state.isSaved = action.payload;
    },
    setWorkflowIsDeleted: (state, action: PayloadAction<boolean>) => {
      state.isDeleted = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadExistingWorkflow, (state, _action) => {
      state.isSaved = true;
    });

    builder.addMatcher(
      (action) => actionTypesThatUnlockSaving.includes(action.type),
      (state, _action) => {
        state.isSaved = false;
      }
    );
  },
});

export const {
  changeScreenPosition,
  changeSelectionDragState,
  openSelectionPanel,
  closeSelectionPanel,
  setWorkflowIsSaved,
  setWorkflowIsDeleted,
  setCurrentTimeConstraintConnectionInProgress,
  setNewlyCreatedTimeConstraintId,
  setTaskDependenciesAreVisible,
} = uiSlice.actions;

export default uiSlice.reducer;
